// src/components/InlineGlossPopover.jsx
//
// GlossTerm: an inline, ANCHORED popover for a highlighted phrase in body text -- used
// by examiner-comment notes (LitEssaySection) and pack-annotation glossary boxes
// (AnnotatedText). The popover appears NEXT TO the word and works on BOTH desktop (hover)
// and mobile (tap toggles + pins). Outside click / Esc closes.
//
// WHY A PORTAL: the reader root keeps a lingering transform from `.page-fade-in`
// (animation fill-mode: forwards ends at translateY(0)), and section cards/stagger items
// add their own. A non-`none` transform on an ancestor makes the anchored popover a
// trapped, lower-painted member of that stacking context, so a LATER section's text
// painted OVER it (the reported "annotation behind the words" bug). Rendering the popover
// via a portal to document.body with position:fixed coords (useFixedPopoverPosition)
// escapes every ancestor stacking/overflow/transform context so it always sits on top --
// the same pattern as ReaderAiDrawer / ReaderNotesDrawer / the model-essay modal. Portals
// preserve React context but NOT CSS ancestor inheritance, so we re-apply the reader's
// day/night skin (ReadingPrefsContext) on the portaled root.
import React, { useState, useRef, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useFixedPopoverPosition } from '../hooks/useFixedPopoverPosition.js';
import { ReadingPrefsContext } from '../pages/useReadingPrefs.js';
import { portalSkinClass } from '../pages/readingPrefs.js';

export default function GlossTerm({ children, popover, label }) {
    const [pinned, setPinned] = useState(false);   // tap/click-pinned (mobile + desktop)
    const [hovered, setHovered] = useState(false);  // desktop hover preview
    const open = (pinned || hovered) && !!popover;

    // Fixed-position coords for the portaled popover (centered under the trigger, flips
    // above near the viewport bottom, clamped to the viewport). `ready` hides it until the
    // first measure so it never flashes at (0,0).
    const { triggerRef, tooltipRef, pos, ready } = useFixedPopoverPosition(open, 256);
    const { colorMode } = useContext(ReadingPrefsContext);
    const skin = portalSkinClass(colorMode);

    // While pinned, close on an outside click or Esc. The popover is PORTALED out of this
    // span, so an "outside" check must consider BOTH the trigger AND the detached tooltip.
    useEffect(() => {
        if (!pinned) return undefined;
        const onDoc = (e) => {
            if (triggerRef.current && triggerRef.current.contains(e.target)) return;
            if (tooltipRef.current && tooltipRef.current.contains(e.target)) return;
            setPinned(false);
        };
        const onEsc = (e) => { if (e.key === 'Escape') setPinned(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onEsc);
        return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
    }, [pinned, triggerRef, tooltipRef]);

    const toggle = (e) => { e.stopPropagation(); setPinned((p) => !p); };

    return (
        <span className="inline">
            <span
                ref={triggerRef}
                role="button" tabIndex={0}
                aria-label={label || undefined}
                onClick={toggle}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPinned((p) => !p); } }}
                className="border-b border-dashed border-book-accent/70 text-book-accent cursor-pointer hover:bg-book-accent/10 transition-colors"
            >
                {children}
            </span>
            {open && typeof document !== 'undefined' && createPortal(
                <div className={skin}>
                    <div
                        ref={tooltipRef}
                        onClick={(e) => e.stopPropagation()}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        style={{ position: 'fixed', left: (pos && pos.left) || 0, top: (pos && pos.top) || 0, visibility: ready ? 'visible' : 'hidden' }}
                        className="z-[120] w-64 max-w-[80vw] cursor-default bg-book-pageDark border border-book-accent/60 shadow-2xl p-3 text-left normal-case whitespace-normal"
                    >
                        {popover}
                    </div>
                </div>,
                document.body,
            )}
        </span>
    );
}
